import React, { useEffect, useState } from "react"
import { Button, message } from "antd"
import { FileTextOutlined } from "@ant-design/icons"
import GetLabel from "DisplayFramework/components/GetLabel"

interface DownloadPDFProps {
  pageName?: string
  contentElementId?: string
  label?: string
  className?: string
}

const DownloadPDF = ({ pageName, contentElementId, label, className }: DownloadPDFProps) => {
  const [loading, setLoading] = useState(false)
  const [pdfReady, setPdfReady] = useState(false)

  useEffect(() => {
    const scriptId = "html2pdf-script"
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script")
      script.id = scriptId
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"
      script.onload = () => {
        console.log("html2pdf.js loaded")
        setPdfReady(true)
      }
      script.onerror = () => {
        message.error("Failed to load PDF library.")
      }
      document.body.appendChild(script)
    } else {
      setPdfReady(true)
    }
  }, [])

  const handleDownloadPDF = async () => {
    const element = document.getElementById(contentElementId ?? "pdf-content")

    if (!pdfReady || !window.html2pdf) {
      message.error("PDF library not ready yet. Please try again shortly.")
      return
    }

    if (!element) {
      message.error("PDF content not found. Make sure #pdf-content exists.")
      return
    }

    setLoading(true)

    try {
      // Wait a moment for any dynamic content to render
      await new Promise((resolve) => setTimeout(resolve, 200))

      const opt = {
        margin: 0.3,
        filename: `${pageName || "page"}.pdf`,
        image: { type: "png", quality: 2 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          windowHeight: element.scrollHeight,
        },
        jsPDF: {
          unit: "in",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      }

      console.log("Generating PDF for element:", element)
      console.log("Element dimensions:", {
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight,
      })

      await window.html2pdf().set(opt).from(element).save()
      message.success("PDF downloaded successfully")
    } catch (error) {
      console.error("PDF generation error:", error)
      message.error("Failed to generate PDF")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      icon={<FileTextOutlined />}
      className={`save-button ${className}`}
      onClick={handleDownloadPDF}
      loading={loading}
      disabled={!pdfReady}
    >
      <GetLabel label={label ?? "downloadPDF"} />
    </Button>
  )
}

export default DownloadPDF

declare global {
  interface Window {
    html2pdf: any
  }
}


// import React, { useState } from "react"
// import { Button, message } from "antd"
// import { FileTextOutlined } from "@ant-design/icons"
// import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer"
// import GetLabel from "DisplayFramework/components/GetLabel"

// interface AntdPDFExtractorProps {
//   pageName?: string
//   label?: string
//   contentElementId?: string
// }

// // Enhanced PDF Styles
// const styles = StyleSheet.create({
//   page: {
//     flexDirection: "column",
//     backgroundColor: "#FFFFFF",
//     padding: 30,
//     fontSize: 10,
//     lineHeight: 1.4,
//     fontFamily: "Helvetica",
//   },
//   title: {
//     fontSize: 16,
//     marginBottom: 15,
//     fontWeight: "bold",
//     color: "#1890ff", // Ant Design blue
//     borderBottom: "1 solid #1890ff",
//     paddingBottom: 5,
//   },
//   sectionTitle: {
//     fontSize: 14,
//     marginTop: 15,
//     marginBottom: 10,
//     fontWeight: "bold",
//     color: "#1890ff",
//   },
//   twoColumnContainer: {
//     flexDirection: "row",
//     marginBottom: 15,
//     gap: 20,
//   },
//   column: {
//     flex: 1,
//   },
//   labelValuePair: {
//     flexDirection: "row",
//     marginBottom: 6,
//     alignItems: "flex-start",
//   },
//   label: {
//     fontSize: 10,
//     fontWeight: "bold",
//     width: 100,
//     color: "#595959",
//   },
//   value: {
//     fontSize: 10,
//     flex: 1,
//     color: "#262626",
//   },
//   tableContainer: {
//     marginBottom: 15,
//   },
//   tableHeader: {
//     flexDirection: "row",
//     backgroundColor: "#fafafa",
//     borderBottom: "1 solid #d9d9d9",
//     padding: 6,
//     marginBottom: 5,
//   },
//   tableRow: {
//     flexDirection: "row",
//     borderBottom: "0.5 solid #f0f0f0",
//     padding: 6,
//     marginBottom: 3,
//     backgroundColor: "#ffffff",
//   },
//   tableCell: {
//     flex: 1,
//     fontSize: 9,
//     paddingHorizontal: 2,
//   },
//   tableCellHeader: {
//     flex: 1,
//     fontSize: 9,
//     fontWeight: "bold",
//     paddingHorizontal: 2,
//     color: "#595959",
//   },
//   cardRow: {
//     flexDirection: "row",
//     marginBottom: 8,
//     padding: 8,
//     backgroundColor: "#fafafa",
//     border: "1 solid #f0f0f0",
//   },
//   cardCell: {
//     flex: 1,
//     fontSize: 9,
//     paddingHorizontal: 3,
//   },
//   emptyState: {
//     textAlign: "center",
//     fontSize: 10,
//     color: "#8c8c8c",
//     fontStyle: "italic",
//     marginBottom: 10,
//   },
// })

// interface ExtractedContent {
//   sections: Array<{
//     title: string
//     type: "general" | "rates" | "fees" | "amortization" | "triggers" | "exit" | "conversions"
//     data: any
//   }>
// }

// // Function to extract content from Ant Design structure
// const extractAntdContent = (element: HTMLElement): ExtractedContent => {
//   const content: ExtractedContent = { sections: [] }

//   // Find all sections by looking for AntTitle elements with color-blue class
//   const sectionTitles = element.querySelectorAll(".color-blue")

//   sectionTitles.forEach((titleElement) => {
//     const sectionTitle = titleElement.textContent?.trim()
//     if (!sectionTitle) return

//     // Determine section type based on title content
//     let sectionType: ExtractedContent["sections"][0]["type"] = "general"
//     const titleLower = sectionTitle.toLowerCase()

//     if (titleLower.includes("général") || titleLower.includes("overall")) {
//       sectionType = "general"
//     } else if (titleLower.includes("taux") || titleLower.includes("rates")) {
//       sectionType = "rates"
//     } else if (titleLower.includes("frais") || titleLower.includes("fees")) {
//       sectionType = "fees"
//     } else if (titleLower.includes("amortis") || titleLower.includes("amortization")) {
//       sectionType = "amortization"
//     } else if (titleLower.includes("trigger") || titleLower.includes("déclench")) {
//       sectionType = "triggers"
//     } else if (titleLower.includes("sortie") || titleLower.includes("exit")) {
//       sectionType = "exit"
//     } else if (titleLower.includes("conversion")) {
//       sectionType = "conversions"
//     }

//     // Extract data based on section type
//     let sectionData: any = {}

//     try {
//       if (sectionType === "general") {
//         sectionData = extractGeneralInfo(titleElement)
//       } else if (sectionType === "rates") {
//         sectionData = extractRatesInfo(titleElement)
//         console.log("Extracted rates data:", sectionData) // Debug log
//       } else if (sectionType === "fees") {
//         sectionData = extractFeesInfo(titleElement)
//         console.log("Extracted fees data:", sectionData) // Debug log
//       } else if (sectionType === "amortization") {
//         sectionData = extractAmortizationInfo(titleElement)
//         console.log("Extracted amortization data:", sectionData) // Debug log
//       } else if (sectionType === "triggers") {
//         sectionData = extractTriggersInfo(titleElement)
//         console.log("Extracted triggers data:", sectionData) // Debug log
//       } else if (sectionType === "exit") {
//         sectionData = extractExitInfo(titleElement)
//       } else if (sectionType === "conversions") {
//         sectionData = extractConversionsInfo(titleElement)
//         console.log("Extracted conversions data:", sectionData) // Debug log
//       }
//     } catch (error) {
//       console.error(`Error extracting ${sectionType} data:`, error)
//       sectionData = []
//     }

//     content.sections.push({
//       title: sectionTitle,
//       type: sectionType,
//       data: sectionData,
//     })
//   })

//   return content
// }

// // Helper functions to extract specific section data
// const extractGeneralInfo = (titleElement: Element) => {
//   const pairs: Array<{ label: string; value: string }> = []

//   // Find the parent container and look for label-value pairs
//   let container = titleElement.parentElement
//   while (container && !container.querySelector(".mb-3")) {
//     container = container.parentElement
//   }

//   if (container) {
//     const labelValuePairs = container.querySelectorAll(".mb-3")
//     labelValuePairs.forEach((pair) => {
//       const labelElement = pair.querySelector(".font-weight-400")
//       const valueElement = pair.querySelector(".font-weight-500")

//       if (labelElement && valueElement) {
//         const label = labelElement.textContent?.trim().replace(":", "") || ""
//         const value = valueElement.textContent?.trim() || ""
//         if (label && value) {
//           pairs.push({ label, value })
//         }
//       }
//     })
//   }

//   return pairs
// }

// const extractRatesInfo = (titleElement: Element) => {
//   const rates: Array<any> = []

//   // Find the parent container of the title
//   let container = titleElement.closest(".mb-6, .container-stepper, div")
//   if (!container) container = titleElement.parentElement

//   // Look for activity cards in the entire container, not just next siblings
//   const activityCards = container?.querySelectorAll(".activity-card") || []

//   activityCards.forEach((card) => {
//     // Check if this card is in the rates section by looking at its position relative to the title
//     const cardPosition = card.compareDocumentPosition(titleElement)
//     if (cardPosition & Node.DOCUMENT_POSITION_PRECEDING) {
//       // This card comes after the title element
//       const cols = card.querySelectorAll('.ant-col, [class*="span"]')

//       if (cols.length >= 6) {
//         // Extract text content more thoroughly
//         const getColText = (col: Element) => {
//           return col.textContent?.trim().replace(/\s+/g, " ") || "-"
//         }

//         rates.push({
//           period: getColText(cols[0]),
//           startDay: getColText(cols[1]),
//           nbDays: getColText(cols[2]),
//           endDay: getColText(cols[3]),
//           nscCondition: getColText(cols[4]),
//           clientCondition: getColText(cols[5]),
//         })
//       }
//     }
//   })

//   return rates
// }

// const extractFeesInfo = (titleElement: Element) => {
//   const fees: Array<any> = []

//   let container = titleElement.closest(".mb-6, .container-stepper, div")
//   if (!container) container = titleElement.parentElement

//   const activityCards = container?.querySelectorAll(".activity-card") || []

//   activityCards.forEach((card) => {
//     const cardPosition = card.compareDocumentPosition(titleElement)
//     if (cardPosition & Node.DOCUMENT_POSITION_PRECEDING) {
//       const cols = card.querySelectorAll('.ant-col, [class*="span"]')

//       if (cols.length >= 8) {
//         const getColText = (col: Element) => {
//           return col.textContent?.trim().replace(/\s+/g, " ") || "-"
//         }

//         fees.push({
//           type: getColText(cols[0]),
//           category: getColText(cols[1]),
//           frequency: getColText(cols[2]),
//           startDay: getColText(cols[3]),
//           endDay: getColText(cols[4]),
//           nbDays: getColText(cols[5]),
//           firstBilling: getColText(cols[6]),
//           amountRate: getColText(cols[7]),
//           billingTo: cols[8] ? getColText(cols[8]) : "-",
//         })
//       }
//     }
//   })

//   return fees
// }

// const extractAmortizationInfo = (titleElement: Element) => {
//   const amortization: Array<any> = []

//   let container = titleElement.closest(".mb-6, .container-stepper, div")
//   if (!container) container = titleElement.parentElement

//   const activityCards = container?.querySelectorAll(".activity-card") || []

//   activityCards.forEach((card) => {
//     const cardPosition = card.compareDocumentPosition(titleElement)
//     if (cardPosition & Node.DOCUMENT_POSITION_PRECEDING) {
//       const cols = card.querySelectorAll('.ant-col, [class*="span"]')

//       if (cols.length >= 5) {
//         const getColText = (col: Element) => {
//           return col.textContent?.trim().replace(/\s+/g, " ") || "-"
//         }

//         amortization.push({
//           startDay: getColText(cols[0]),
//           endDay: getColText(cols[1]),
//           periodicity: getColText(cols[2]),
//           billingDay: getColText(cols[3]),
//           rate: getColText(cols[4]),
//         })
//       }
//     }
//   })

//   return amortization
// }

// const extractTriggersInfo = (titleElement: Element) => {
//   const triggers: Array<any> = []

//   let container = titleElement.closest(".mb-6, .container-stepper, div")
//   if (!container) container = titleElement.parentElement

//   const activityCards = container?.querySelectorAll(".activity-card") || []

//   activityCards.forEach((card) => {
//     const cardPosition = card.compareDocumentPosition(titleElement)
//     if (cardPosition & Node.DOCUMENT_POSITION_PRECEDING) {
//       const cols = card.querySelectorAll('.ant-col, [class*="span"]')

//       if (cols.length >= 4) {
//         const getColText = (col: Element) => {
//           return col.textContent?.trim().replace(/\s+/g, " ") || "-"
//         }

//         triggers.push({
//           eventId: getColText(cols[0]),
//           timeDelay: getColText(cols[1]),
//           consequence: getColText(cols[2]),
//           offerId: getColText(cols[3]),
//         })
//       }
//     }
//   })

//   return triggers
// }

// const extractExitInfo = (titleElement: Element) => {
//   const exitInfo: Array<{ label: string; value: string }> = []

//   let nextElement = titleElement.parentElement?.parentElement?.nextElementSibling
//   while (nextElement) {
//     const labelValuePairs = nextElement.querySelectorAll(".mb-3")
//     labelValuePairs.forEach((pair) => {
//       const labelElement = pair.querySelector(".font-weight-400")
//       const valueElement = pair.querySelector(".font-weight-500")

//       if (labelElement && valueElement) {
//         const label = labelElement.textContent?.trim().replace(":", "") || ""
//         const value = valueElement.textContent?.trim() || ""
//         if (label && value) {
//           exitInfo.push({ label, value })
//         }
//       }
//     })
//     nextElement = nextElement.nextElementSibling
//     if (nextElement?.querySelector(".color-blue")) break
//   }

//   return exitInfo
// }

// const extractConversionsInfo = (titleElement: Element) => {
//   const conversions: Array<any> = []

//   let container = titleElement.closest(".mb-6, .container-stepper, div")
//   if (!container) container = titleElement.parentElement

//   const activityCards = container?.querySelectorAll(".activity-card") || []

//   activityCards.forEach((card) => {
//     const cardPosition = card.compareDocumentPosition(titleElement)
//     if (cardPosition & Node.DOCUMENT_POSITION_PRECEDING) {
//       const cols = card.querySelectorAll('.ant-col, [class*="span"]')

//       if (cols.length >= 3) {
//         const getColText = (col: Element) => {
//           return col.textContent?.trim().replace(/\s+/g, " ") || "-"
//         }

//         conversions.push({
//           productFamily: getColText(cols[0]),
//           productName: getColText(cols[1]),
//           description: getColText(cols[2]),
//         })
//       }
//     }
//   })

//   return conversions
// }

// // PDF Document Component
// const AntdPDFDocument = ({ content }: { content: ExtractedContent }) => (
//   <Document>
//     <Page size="A4" style={styles.page}>
//       {content.sections.map((section, index) => (
//         <View key={index}>
//           <Text style={styles.sectionTitle}>{section.title}</Text>

//           {section.type === "general" && (
//             <View style={styles.twoColumnContainer}>
//               <View style={styles.column}>
//                 {section.data.slice(0, Math.ceil(section.data.length / 2)).map((pair: any, pairIndex: number) => (
//                   <View key={pairIndex} style={styles.labelValuePair}>
//                     <Text style={styles.label}>{pair.label}:</Text>
//                     <Text style={styles.value}>{pair.value}</Text>
//                   </View>
//                 ))}
//               </View>
//               <View style={styles.column}>
//                 {section.data.slice(Math.ceil(section.data.length / 2)).map((pair: any, pairIndex: number) => (
//                   <View key={pairIndex} style={styles.labelValuePair}>
//                     <Text style={styles.label}>{pair.label}:</Text>
//                     <Text style={styles.value}>{pair.value}</Text>
//                   </View>
//                 ))}
//               </View>
//             </View>
//           )}

//           {section.type === "rates" && (
//             <View style={styles.tableContainer}>
//               <View style={styles.tableHeader}>
//                 <Text style={styles.tableCellHeader}>Période</Text>
//                 <Text style={styles.tableCellHeader}>Début</Text>
//                 <Text style={styles.tableCellHeader}>Nb. jours</Text>
//                 <Text style={styles.tableCellHeader}>Fin</Text>
//                 <Text style={styles.tableCellHeader}>Conditions NSC</Text>
//                 <Text style={styles.tableCellHeader}>Conditions client</Text>
//               </View>
//               {section.data.length === 0 ? (
//                 <Text style={styles.emptyState}>Aucun taux configuré</Text>
//               ) : (
//                 section.data.map((rate: any, rateIndex: number) => (
//                   <View key={rateIndex} style={styles.tableRow}>
//                     <Text style={styles.tableCell}>{rate.period}</Text>
//                     <Text style={styles.tableCell}>{rate.startDay}</Text>
//                     <Text style={styles.tableCell}>{rate.nbDays}</Text>
//                     <Text style={styles.tableCell}>{rate.endDay}</Text>
//                     <Text style={styles.tableCell}>{rate.nscCondition}</Text>
//                     <Text style={styles.tableCell}>{rate.clientCondition}</Text>
//                   </View>
//                 ))
//               )}
//             </View>
//           )}

//           {section.type === "fees" && (
//             <View style={styles.tableContainer}>
//               <View style={styles.tableHeader}>
//                 <Text style={styles.tableCellHeader}>Type</Text>
//                 <Text style={styles.tableCellHeader}>Catégorie</Text>
//                 <Text style={styles.tableCellHeader}>Fréquence</Text>
//                 <Text style={styles.tableCellHeader}>Début</Text>
//                 <Text style={styles.tableCellHeader}>Fin</Text>
//                 <Text style={styles.tableCellHeader}>Nb. jours</Text>
//                 <Text style={styles.tableCellHeader}>Montant/Taux</Text>
//                 <Text style={styles.tableCellHeader}>Facturation</Text>
//               </View>
//               {section.data.length === 0 ? (
//                 <Text style={styles.emptyState}>Aucun frais configuré</Text>
//               ) : (
//                 section.data.map((fee: any, feeIndex: number) => (
//                   <View key={feeIndex} style={styles.tableRow}>
//                     <Text style={styles.tableCell}>{fee.type}</Text>
//                     <Text style={styles.tableCell}>{fee.category}</Text>
//                     <Text style={styles.tableCell}>{fee.frequency}</Text>
//                     <Text style={styles.tableCell}>{fee.startDay}</Text>
//                     <Text style={styles.tableCell}>{fee.endDay}</Text>
//                     <Text style={styles.tableCell}>{fee.nbDays}</Text>
//                     <Text style={styles.tableCell}>{fee.amountRate}</Text>
//                     <Text style={styles.tableCell}>{fee.billingTo}</Text>
//                   </View>
//                 ))
//               )}
//             </View>
//           )}

//           {section.type === "amortization" && (
//             <View style={styles.tableContainer}>
//               <View style={styles.tableHeader}>
//                 <Text style={styles.tableCellHeader}>Début</Text>
//                 <Text style={styles.tableCellHeader}>Fin</Text>
//                 <Text style={styles.tableCellHeader}>Périodicité</Text>
//                 <Text style={styles.tableCellHeader}>Jour facturation</Text>
//                 <Text style={styles.tableCellHeader}>Taux</Text>
//               </View>
//               {section.data.length === 0 ? (
//                 <Text style={styles.emptyState}>Aucun amortissement configuré</Text>
//               ) : (
//                 section.data.map((amort: any, amortIndex: number) => (
//                   <View key={amortIndex} style={styles.tableRow}>
//                     <Text style={styles.tableCell}>{amort.startDay}</Text>
//                     <Text style={styles.tableCell}>{amort.endDay}</Text>
//                     <Text style={styles.tableCell}>{amort.periodicity}</Text>
//                     <Text style={styles.tableCell}>{amort.billingDay}</Text>
//                     <Text style={styles.tableCell}>{amort.rate}</Text>
//                   </View>
//                 ))
//               )}
//             </View>
//           )}

//           {section.type === "triggers" && (
//             <View style={styles.tableContainer}>
//               <View style={styles.tableHeader}>
//                 <Text style={styles.tableCellHeader}>Event ID</Text>
//                 <Text style={styles.tableCellHeader}>Délai</Text>
//                 <Text style={styles.tableCellHeader}>Conséquence</Text>
//                 <Text style={styles.tableCellHeader}>Offer ID</Text>
//               </View>
//               {section.data.length === 0 ? (
//                 <Text style={styles.emptyState}>Aucun trigger configuré</Text>
//               ) : (
//                 section.data.map((trigger: any, triggerIndex: number) => (
//                   <View key={triggerIndex} style={styles.tableRow}>
//                     <Text style={styles.tableCell}>{trigger.eventId}</Text>
//                     <Text style={styles.tableCell}>{trigger.timeDelay}</Text>
//                     <Text style={styles.tableCell}>{trigger.consequence}</Text>
//                     <Text style={styles.tableCell}>{trigger.offerId}</Text>
//                   </View>
//                 ))
//               )}
//             </View>
//           )}

//           {section.type === "exit" && (
//             <View>
//               {section.data.map((pair: any, pairIndex: number) => (
//                 <View key={pairIndex} style={styles.labelValuePair}>
//                   <Text style={styles.label}>{pair.label}:</Text>
//                   <Text style={styles.value}>{pair.value}</Text>
//                 </View>
//               ))}
//             </View>
//           )}

//           {section.type === "conversions" && (
//             <View style={styles.tableContainer}>
//               {section.data.length === 0 ? (
//                 <Text style={styles.emptyState}>Aucune conversion configurée</Text>
//               ) : (
//                 section.data.map((conversion: any, convIndex: number) => (
//                   <View key={convIndex} style={styles.cardRow}>
//                     <View style={styles.cardCell}>
//                       <Text style={styles.label}>Famille produit:</Text>
//                       <Text style={styles.value}>{conversion.productFamily}</Text>
//                     </View>
//                     <View style={styles.cardCell}>
//                       <Text style={styles.label}>Nom produit:</Text>
//                       <Text style={styles.value}>{conversion.productName}</Text>
//                     </View>
//                     <View style={styles.cardCell}>
//                       <Text style={styles.label}>Description:</Text>
//                       <Text style={styles.value}>{conversion.description}</Text>
//                     </View>
//                   </View>
//                 ))
//               )}
//             </View>
//           )}
//         </View>
//       ))}
//     </Page>
//   </Document>
// )

// const DownloadPDF = ({ pageName = "document", label, contentElementId = "pdf-content" }: AntdPDFExtractorProps) => {
//   const [loading, setLoading] = useState(false)

//   const handleDownloadPDF = async () => {
//     setLoading(true)
//     try {
//       const element = document.getElementById(contentElementId)
//       if (!element) {
//         message.error(`Element with ID '${contentElementId}' not found.`)
//         return
//       }

//       const extractedContent = extractAntdContent(element)

//       if (extractedContent.sections.length === 0) {
//         message.error("No content sections found to convert to PDF.")
//         return
//       }

//       const blob = await pdf(<AntdPDFDocument content={extractedContent} />).toBlob()
//       const url = URL.createObjectURL(blob)
//       const link = document.createElement("a")
//       link.href = url
//       link.download = `${pageName}.pdf`
//       document.body.appendChild(link)
//       link.click()
//       document.body.removeChild(link)
//       URL.revokeObjectURL(url)

//       message.success("PDF downloaded successfully")
//     } catch (error) {
//       console.error("PDF generation error:", error)
//       message.error("Failed to generate PDF")
//     } finally {
//       setLoading(false)
//     }
//   }

//   return (
//     <Button icon={<FileTextOutlined />} className="save-button" onClick={handleDownloadPDF} loading={loading}>
//       <GetLabel label={label ?? "downloadPDF"} />
//     </Button>
//   )
// }

// export default DownloadPDF
